import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient, MessageType, stringifyMessage } from 'graphql-ws'
import { getOrFetchAccessToken } from '../../auth/token'

let pingSentTime

// Connection state that can be observed by React components
export const connectionState = {
    isConnected: false,
    hasConnectedOnce: false, // Track if we've ever successfully connected
    listeners: new Set(),
    authFailedListeners: new Set(),

    setConnected(connected) {
        this.isConnected = connected
        if (connected) {
            this.hasConnectedOnce = true
        }
        this.listeners.forEach(listener => listener(connected, this.hasConnectedOnce))
    },

    signalAuthFailed() {
        console.log('WebSocket: signaling auth failure to listeners')
        this.authFailedListeners.forEach(listener => listener())
    },

    subscribe(listener) {
        this.listeners.add(listener)
        // Immediately call with current state
        listener(this.isConnected, this.hasConnectedOnce)
        return () => this.listeners.delete(listener)
    },

    onAuthFailed(listener) {
        this.authFailedListeners.add(listener)
        return () => this.authFailedListeners.delete(listener)
    }
}

export const wsLink = new GraphQLWsLink(createClient({
    connectionParams: async () => {
        // Attempt to get a fresh access token (refreshes via refresh token if needed)
        try {
            const token = await getOrFetchAccessToken()
            return { authToken: token }
        } catch (error) {
            console.error('WebSocket: failed to obtain access token for connection:', error)
            // Fall back to whatever is in storage; server will reject with 4401 if invalid
            return { authToken: localStorage.getItem('at') }
        }
    },
    keepAlive: 30_000, // 30 seconds - more responsive to connection issues
    connectionAckTimeout: 10_000, // 10 seconds to receive ack from server
    retryAttempts: Infinity, // Keep retrying
    retryWait: async (retries) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
        const delay = Math.min(1000 * Math.pow(2, retries), 30_000)
        console.log(`WebSocket retry attempt ${retries + 1}, waiting ${delay}ms`)
        await new Promise(resolve => setTimeout(resolve, delay))
    },
    on: {
        closed: (event) => {
            logEvent('closed', { event })
            connectionState.setConnected(false)
            // 4401 Unauthorized: server rejected the connection due to invalid/expired auth
            if (event?.code === 4401) {
                connectionState.signalAuthFailed()
            }
        },
        connected: (socket, payload, wasRetry) => {
            logEvent('connected', { socket, payload, wasRetry })
            connectionState.setConnected(true)
            pingSentTime = Date.now()
            socket.send(
                stringifyMessage({
                    type: MessageType.Ping,
                    payload,
                }),
            )
        },
        connecting: (isRetry) => logEvent('connecting', { isRetry }),
        error: (error) => {
            logEvent('error', { error })
            connectionState.setConnected(false)
        },
        message: (message) => logEvent('message', { message }),
        opened: (socket) => logEvent('opened', { socket }),
        ping: (received, payload) => logEvent('ping', { received, payload }),
        pong: (received, payload) => {
            if (received && !global.measuredRTT) {
                const now = Date.now()
                global.measuredRTT = now - pingSentTime
                global.serverOffset = Math.floor(payload.time + (global.measuredRTT / 2) - now)
                console.log(`  server time: ${payload.time}`)
                console.log(`     our time: ${now}`)
                console.log(` measured RTT: ${global.measuredRTT}`)
                console.log(`server offset: ${global.serverOffset}`)
            }
            logEvent('pong', { received, payload })
        }
    },
    url: process.env.REACT_APP_WEBSOCKET_URL
}))

function logEvent(type, data) {
    let value
    switch (type) {
        case 'closed':
            const { event } = data
            value = `code: ${event.code} reason: ${event.reason} wasClean: ${event.wasClean}`
            break
        case 'connected':
            value = `payload: ${JSON.stringify(data.payload)}`
            break
        case 'connecting':
            value = `isRetry: ${data.isRetry}`
            break
        case 'error':
            const { message, stack } = data.error
            value = `message: ${message} stack: ${stack}`
            break
        case 'message': {
            const { payload, type } = data.message
            value = `type: ${type} payload: ${JSON.stringify(payload)}`
            break
        }
        case 'opened':
            value = ''
            break
        case 'ping': {
            const { received, payload } = data
            value = `received: ${received} payload: ${JSON.stringify(payload)}`
            break
        }
        case 'pong': {
            const { received, payload } = data
            value = `received: ${received} payload: ${JSON.stringify(payload)}`
            break
        }
        default:
            value = 'unknown type'
    }
    // console.log(`websocket: ${type}: ${value}`)
}
